/**
 * WHYNOT? behaviour layer.
 *
 * Ports the three pieces of interactivity the Lovable build relied on React for:
 * the scroll reveal, the Shop by Style image swap, and the mobile buy bar.
 * Everything re-initialises on Shopify section load so it survives the theme editor.
 */

function wnReveal(root) {
  const targets = root.querySelectorAll('.wn-reveal:not([data-wn-observed])');
  if (!targets.length) return;

  if (!('IntersectionObserver' in window)) {
    targets.forEach((el) => el.setAttribute('data-visible', 'true'));
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.setAttribute('data-visible', 'true');
        io.unobserve(entry.target);
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
  );

  targets.forEach((el) => {
    el.setAttribute('data-wn-observed', '');
    io.observe(el);
  });
}

/**
 * Hovering a style name crossfades the large editorial image beside it.
 * Pointer devices only — on touch the section falls back to the swipe rail.
 */
function wnShopByStyle(root) {
  root.querySelectorAll('[data-wn-styles]:not([data-wn-bound])').forEach((section) => {
    section.setAttribute('data-wn-bound', '');

    const images = section.querySelectorAll('.wn-styles__stage-img');
    const triggers = section.querySelectorAll('[data-wn-style-trigger]');
    const captionTitle = section.querySelector('[data-wn-style-title]');
    const captionCopy = section.querySelector('[data-wn-style-copy]');

    const activate = (index) => {
      images.forEach((img, i) => img.setAttribute('data-active', String(i === index)));
      triggers.forEach((trigger, i) => trigger.setAttribute('data-active', String(i === index)));
      const trigger = triggers[index];
      if (!trigger) return;
      if (captionTitle) captionTitle.textContent = trigger.dataset.styleTitle || '';
      if (captionCopy) captionCopy.textContent = trigger.dataset.styleCopy || '';
    };

    triggers.forEach((trigger, i) => {
      trigger.addEventListener('mouseenter', () => activate(i));
      trigger.addEventListener('focus', () => activate(i));
    });
  });
}

/**
 * Hero banner slideshow: crossfades the campaign artwork.
 *
 * The banners carry their own typography, so a slide that is fading out must also leave the
 * tab order — otherwise keyboard users land on links they cannot see.
 */
function wnHero(root) {
  root.querySelectorAll('[data-wn-hero]:not([data-wn-bound])').forEach((hero) => {
    hero.setAttribute('data-wn-bound', '');

    const slides = hero.querySelectorAll('[data-wn-hero-slide]');
    const dots = hero.querySelectorAll('[data-wn-hero-dot]');
    if (slides.length < 2) return;

    const interval = Number(hero.dataset.interval) || 6000;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let index = 0;
    let timer = null;

    const show = (next) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const active = i === index;
        slide.setAttribute('data-active', String(active));
        const link = slide.querySelector('.wn-hero__slide-link');
        if (link) link.tabIndex = active ? 0 : -1;
      });
      dots.forEach((dot, i) => dot.setAttribute('aria-selected', String(i === index)));
    };

    const stop = () => {
      clearInterval(timer);
      timer = null;
    };

    const start = () => {
      if (timer || reduced || hero.dataset.autoplay !== 'true') return;
      timer = setInterval(() => show(index + 1), interval);
    };

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        show(i);
        stop();
        start();
      });
    });

    // Let people read a banner they are pointing at, or one they have tabbed into.
    hero.addEventListener('mouseenter', stop);
    hero.addEventListener('mouseleave', start);
    hero.addEventListener('focusin', stop);
    hero.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));

    start();
  });
}

/**
 * Colour swatches on the product card.
 *
 * The card shows one "Agregar al carrito" button rather than Horizon's choose-options
 * flow, so picking a swatch has to point that button at the right variant — otherwise it
 * would always add the first colour no matter which dot the shopper clicked.
 * Without JS the swatches stay plain links to the variant URL.
 */
function wnSwatches(root) {
  root.querySelectorAll('[data-wn-swatches]:not([data-wn-bound])').forEach((group) => {
    group.setAttribute('data-wn-bound', '');

    const card = group.closest('.wn-card');
    const label = group.querySelector('[data-wn-swatch-label]');
    const swatches = group.querySelectorAll('.wn-card__swatch');

    swatches.forEach((swatch) => {
      const variantId = swatch.dataset.variantId;
      if (!variantId) return;

      swatch.addEventListener('click', (event) => {
        event.preventDefault();

        swatches.forEach((s) => s.setAttribute('data-active', String(s === swatch)));
        if (label) label.textContent = swatch.dataset.swatchName || '';

        const input = card?.querySelector('.wn-card__action input[name="id"]');
        if (input) {
          input.value = variantId;
          input.removeAttribute('disabled');
        }

        const image = card?.querySelector('.wn-card__img--primary');
        const variantImage = swatch.dataset.variantImage;
        if (image && variantImage) image.src = variantImage;
      });
    });
  });
}

/**
 * Product page: gallery thumbnails, colour selection and buy now.
 *
 * The colour swatches drive the hidden variant input directly rather than going through
 * Horizon's variant picker, so the markup can match the design exactly. Price, availability
 * and the main shot follow the selected variant.
 */
function wnProduct(root) {
  root.querySelectorAll('[data-wn-product]:not([data-wn-bound])').forEach((pdp) => {
    pdp.setAttribute('data-wn-bound', '');

    // Liquid's `image_tag` drops data-* attributes, so the hook sits on the wrapper.
    const stage = pdp.querySelector('[data-wn-pdp-stage] img') || pdp.querySelector('.wn-pdp__image');
    const thumbs = pdp.querySelectorAll('[data-wn-pdp-thumb]');
    const swatches = pdp.querySelectorAll('[data-wn-pdp-swatch]');
    const variantInput = pdp.querySelector('[data-wn-pdp-variant]');
    const priceEl = pdp.querySelector('[data-wn-pdp-price]');
    const installmentsEl = pdp.querySelector('[data-wn-pdp-installments]');
    const colorLabel = pdp.querySelector('[data-wn-pdp-color-label]');
    const freeShipEl = pdp.querySelector('[data-wn-pdp-freeship]');
    const addButton = pdp.querySelector('.wn-pdp__add');

    const showImage = (src) => {
      if (!stage || !src) return;
      // srcset would otherwise win over the new src and keep showing the old shot.
      stage.removeAttribute('srcset');
      stage.src = src;
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        thumbs.forEach((t) => t.setAttribute('data-active', String(t === thumb)));
        showImage(thumb.dataset.full);
      });
    });

    swatches.forEach((swatch) => {
      swatch.addEventListener('click', () => {
        swatches.forEach((s) => s.setAttribute('data-active', String(s === swatch)));
        if (colorLabel) colorLabel.textContent = swatch.dataset.name || '';

        const variantId = swatch.dataset.variantId;
        if (variantId && variantInput) variantInput.value = variantId;

        if (swatch.dataset.image) {
          showImage(swatch.dataset.image);
          // Keep the thumb strip in step: highlight the shot we just moved to, if it is listed.
          thumbs.forEach((t) => {
            t.setAttribute('data-active', String(t.dataset.full === swatch.dataset.image));
          });
        }

        const available = swatch.dataset.available !== 'false';
        if (addButton) addButton.disabled = !available;

        // Prices come pre-formatted from Liquid so they always match the shop's money
        // format — formatting here would drift from what the rest of the page shows.
        if (priceEl && swatch.dataset.priceFormatted) {
          priceEl.textContent = swatch.dataset.priceFormatted;
        }
        if (installmentsEl && swatch.dataset.installmentsFormatted) {
          installmentsEl.textContent = swatch.dataset.installmentsFormatted;
        }
        // El cartel de envio gratis depende del precio, asi que sigue a la variante.
        if (freeShipEl && swatch.dataset.freeship) {
          freeShipEl.hidden = swatch.dataset.freeship !== 'true';
        }
      });
    });

    // "Comprar ahora": add, then go straight to checkout instead of opening the drawer.
    const buyNow = pdp.querySelector('[data-wn-buy-now]');
    const form = pdp.querySelector('form[data-type="add-to-cart-form"]');
    if (buyNow && form) {
      buyNow.addEventListener('click', async () => {
        buyNow.disabled = true;
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantInput?.value, quantity: 1 }),
          });
          if (!response.ok) throw new Error(`cart/add returned ${response.status}`);
          window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
        } catch (error) {
          console.error('[whynot] buy now failed', error);
          buyNow.disabled = false;
          // Fall back to the normal add to cart so the shopper is not stuck.
          form.requestSubmit();
        }
      });
    }
  });
}

/**
 * Sticky mobile buy bar: hidden until the shopper is past the hero.
 */
function wnBuyBar(root) {
  const bar = root.querySelector('[data-wn-buy-bar]:not([data-wn-bound])');
  if (!bar) return;
  bar.setAttribute('data-wn-bound', '');

  const threshold = Number(bar.dataset.wnBuyBar) || 600;
  const sync = () => bar.setAttribute('data-visible', String(window.scrollY > threshold));

  sync();
  window.addEventListener('scroll', sync, { passive: true });
}

/**
 * Comparador before/after. El arrastre, el foco y las flechas del teclado los aporta
 * el <input type="range"> del markup; acá sólo se traduce su valor a la variable CSS
 * que recorta la foto. Si el JS no corre, el comparador queda en su posición inicial
 * y ambas fotos siguen siendo visibles.
 */
function wnBeforeAfter(root) {
  root.querySelectorAll('[data-wn-before-after]:not([data-wn-bound])').forEach((frame) => {
    const range = frame.querySelector('[data-wn-ba-range]');
    if (!range) return;
    frame.setAttribute('data-wn-bound', '');

    const sync = () => frame.style.setProperty('--wn-ba-pos', `${range.value}%`);

    sync();
    range.addEventListener('input', sync);
  });
}

/**
 * Flechas de carrusel para cualquier `.wn-rail` envuelto en `[data-wn-rail]`.
 * El rail ya scrollea solo con swipe y rueda; esto agrega el control explicito
 * para que en desktop se lea como carrusel y no como una fila cortada.
 */
function wnRail(root) {
  root.querySelectorAll('[data-wn-rail]:not([data-wn-bound])').forEach((wrap) => {
    const rail = wrap.querySelector('.wn-rail');
    const prev = wrap.querySelector('[data-wn-rail-prev]');
    const next = wrap.querySelector('[data-wn-rail-next]');
    if (!rail || !prev || !next) return;
    wrap.setAttribute('data-wn-bound', '');

    // Un paso = una tarjeta. Se mide en vivo porque el ancho de la tarjeta cambia
    // por breakpoint y el gap viene del CSS, no de un numero repetido aca.
    const step = () => {
      const card = rail.firstElementChild;
      if (!card) return rail.clientWidth;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    };

    const sync = () => {
      const max = rail.scrollWidth - rail.clientWidth;
      // Sin desborde no hay carrusel: el CSS esconde las flechas con este flag.
      wrap.setAttribute('data-wn-rail', String(max > 1));
      // El margen de 1px absorbe el scrollLeft fraccionario de los zooms del navegador.
      prev.disabled = rail.scrollLeft <= 1;
      next.disabled = rail.scrollLeft >= max - 1;
    };

    prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);

    // Las fotos llegan despues del primer layout y cambian el scrollWidth.
    if ('ResizeObserver' in window) new ResizeObserver(sync).observe(rail);

    sync();
  });
}

/**
 * Each behaviour is isolated: a failure in one must not stop the others, and it must never
 * leave `.wn-reveal` content stuck at opacity 0 with no way back.
 */
function wnInit(root = document) {
  for (const behaviour of [wnReveal, wnHero, wnShopByStyle, wnSwatches, wnProduct, wnBuyBar, wnBeforeAfter, wnRail]) {
    try {
      behaviour(root);
    } catch (error) {
      console.error(`[whynot] ${behaviour.name} failed`, error);
      if (behaviour === wnReveal) {
        root.querySelectorAll('.wn-reveal').forEach((el) => el.setAttribute('data-visible', 'true'));
      }
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => wnInit());
} else {
  wnInit();
}

document.addEventListener('shopify:section:load', (event) => wnInit(event.target));
